import { API_BASE_URL } from "../../constants/index";
import HttpInterface from "../../helpers/HttpInterface";
import { CreateFeedback, UpdateFeedback } from "../../Models/Feedback";
import { defaultFetchParam, FetchParam } from "../../Models/FetchParam";
import { buildAPIUrl } from "../../api/apiHelpers";

export class FeedbackService {
  // Instance of the HttpInterface to make HTTP requests
  private httpProxy: HttpInterface;

  constructor() {
    // Creates an instance of the HttpInterface to handle requests
    this.httpProxy = new HttpInterface();
  }

  /**
   * Fetches all feedback from the registry service API.
   *
   * @param params The query parameters for the API request. Defaults to `defaultFetchParam` if not provided.
   * @returns A promise that resolves with the API response containing the list of feedback.
   */
  all(params: FetchParam = defaultFetchParam): Promise<any> {
    const requestOptions = {
      method: "GET",
    };

    return this.httpProxy.fetch(
      buildAPIUrl(`${API_BASE_URL}/registry-service/feedbacks`, params),
      requestOptions
    );
  }

  /**
   * Fetches a specific feedback by its ID from the API.
   *
   * @param id The unique identifier of the feedback to fetch.
   * @returns A promise that resolves with the API response containing the feedback details.
   */
  get(id: string): Promise<any> {
    const requestOptions = {
      method: "GET",
    };

    return this.httpProxy.fetch(
      `${API_BASE_URL}/registry-service/feedbacks/${id}`,
      requestOptions
    );
  }

  /**
   * Creates a new feedback in the registry service API.
   *
   * @param feedback The details of the feedback to create.
   * @returns A promise that resolves with the API response confirming the creation of the feedback.
   */
  create(feedback: CreateFeedback): Promise<any> {
    const requestOptions = {
      method: "POST",
      body: JSON.stringify(feedback),
    };

    return this.httpProxy.fetch(
      `${API_BASE_URL}/registry-service/feedbacks`,
      requestOptions
    );
  }

  /**
   * Updates an existing feedback in the registry service API.
   *
   * @param feedback The updated details of the feedback.
   * @returns A promise that resolves with the API response confirming the update.
   */
  edit(feedback: UpdateFeedback): Promise<any> {
    const requestOptions = {
      method: "PUT",
      body: JSON.stringify(feedback),
    };

    return this.httpProxy.fetch(
      `${API_BASE_URL}/registry-service/feedbacks/${feedback.id}`,
      requestOptions
    );
  }

  /**
   * Deletes a feedback from the registry service API.
   *
   * @param id The unique identifier of the feedback to delete.
   * @returns A promise that resolves with the API response confirming the deletion.
   */
  delete(id: string): Promise<any> {
    const requestOptions = {
      method: "DELETE",
    };
    return this.httpProxy.fetch(
      `${API_BASE_URL}/registry-service/feedbacks/${id}`,
      requestOptions
    );
  }
}
